import { useEffect, useState } from "react";
import { auth, db, functions } from "../firebase";
import {
  addDoc,
  collection,
  onSnapshot,
  getDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  deleteField,
  setDoc,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  increment,
  limit,
  startAt,
  endAt,
  runTransaction,
} from "firebase/firestore";

// Referencia para facturar la comisión de Notiline aparte (ver ticketSales
// más abajo): el pago de la entrada ya va 100% directo al PayPal del
// organizador, esto solo sirve para calcular cuánto corresponde facturarle
// después. Mismo criterio que STORE_COMMISSION_RATE (storesUtils.js).
export const EVENT_COMMISSION_RATE = 0.05;



export const HORARIO_OPTIONS = [
  { value: "manana", label: "Mañana" },
  { value: "tarde", label: "Tarde" },
  { value: "noche", label: "Noche" },
  { value: "todo_el_dia", label: "Todo el día" },
];

export const getHorarioLabel = (value) =>
  HORARIO_OPTIONS.find((option) => option.value === value)?.label || value || "";

// Link opcional del evento (web, entradas, transmisión). Devuelve "" si está
// vacío, la URL normalizada si es http(s) válida (agrega https:// si falta),
// o null si no es una URL válida. Solo http(s) para que nadie guarde un
// "javascript:" que después se renderiza como href.
export const normalizeEventUrl = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const withProtocol = /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withProtocol);
    if (!["http:", "https:"].includes(url.protocol) || !url.hostname.includes(".")) return null;
    return url.href;
  } catch {
    return null;
  }
};

// Evento pago con cobro por PayPal dentro de Notiline. El correo de PayPal
// es opcional: sin él, el precio es informativo y la entrada se compra en el
// enlace del evento (event.url) o en puerta.
export const sellsTicketsWithPaypal = (event) => !!(event?.isPaid && event?.paypalEmail);

// ---------- Eventos privados ----------
// Las reglas solo dejan listar eventos con una consulta que garantice que
// cada resultado es legible (ver canReadEvent en firestore.rules): los
// públicos con where("isPrivate", "==", false) y los privados propios o
// compartidos con where("participantIds", "array-contains", uid), que
// incluye al organizador (ver publishEvents.js).
export const PUBLIC_EVENTS_FILTER = [["isPrivate", false]];

const createdAtMs = (e) => toJsDate(e.createdAt)?.getTime() || 0;

// Une eventos de varias consultas sin repetir, del más nuevo al más viejo.
export const mergeEventsByCreatedAt = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((e) => byId.set(e.id, e));
  return [...byId.values()].sort((a, b) => createdAtMs(b) - createdAtMs(a));
};

// Eventos privados en los que participa el usuario actual (en vivo).
export const useMyPrivateEvents = () => {
  const [events, setEvents] = useState([]);
  const uid = auth.currentUser?.uid;
  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(
      query(collection(db, "events"), where("participantIds", "array-contains", uid)),
      (snap) => setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (error) => console.error("Error cargando eventos privados:", error)
    );
  }, [uid]);
  return events;
};

// ---------- Fechas de eventos ----------
// Un evento puede tener varias fechas (funciones de una obra, recitales en
// distintas noches): se guardan en `dates` (array de Timestamps) y `date`
// queda con la primera para compatibilidad con las pantallas que solo leen
// una (perfil, mapa, guardados). Eventos viejos solo tienen `date`.
// Una fecha con hora 00:00 se considera "sin hora" (todo ese día).

export const toJsDate = (value) => {
  if (!value) return null;
  if (value.toDate instanceof Function) return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const getEventDates = (event) => {
  const list = Array.isArray(event?.dates) && event.dates.length > 0 ? event.dates : event?.date ? [event.date] : [];
  return list.map(toJsDate).filter(Boolean).sort((a, b) => a - b);
};

export const hasExplicitTime = (d) => d.getHours() !== 0 || d.getMinutes() !== 0;

const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);

// Próxima función que todavía no terminó (una fecha vale hasta el final de ese día).
export const getNextEventDate = (event, now = new Date()) =>
  getEventDates(event).find((d) => endOfDay(d) >= now) || null;

export const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const formatEventDate = (d, { withWeekday = false } = {}) => {
  const day = d.toLocaleDateString("es-AR", {
    ...(withWeekday ? { weekday: "short" } : {}),
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
  return hasExplicitTime(d)
    ? `${day} ${d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} hs`
    : day;
};

// "1/10/2026 21:00 hs · 2/10/2026 21:00 hs" para mostrar todas las fechas.
export const formatEventDatesList = (event) =>
  getEventDates(event).map((d) => formatEventDate(d)).join(" · ");

// Formulario (EventDatesField) <-> Firestore. Cada fila: { day: Date|null, time: "HH:MM"|"" }.
export const eventDatesToRows = (event) => {
  const dates = getEventDates(event);
  if (dates.length === 0) return [{ day: null, time: "" }];
  return dates.map((d) => ({
    day: d,
    time: hasExplicitTime(d) ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` : "",
  }));
};

export const rowsToEventDates = (rows) =>
  rows
    .filter((row) => row.day)
    .map((row) => {
      const d = new Date(row.day);
      const [hh, mm] = (row.time || "").split(":").map(Number);
      d.setHours(Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
      return d;
    })
    .sort((a, b) => a - b);

export const geocodeAddress = async (address) => {
  if (!address || !address.trim()) return null;
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
      { headers: { "Accept-Language": "es" } }
    );
    const data = await res.json();
    if (data.length > 0) {
      return { latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) };
    }
  } catch (error) {
    console.warn("No se pudo geocodificar la dirección:", error);
  }
  return null;
};

export const searchAddressSuggestions = async (query) => {
  if (!query || query.trim().length < 3) return [];
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
      { headers: { "Accept-Language": "es" } }
    );
    const data = await res.json();
    const seenLabels = new Set();
    const suggestions = [];
    data.forEach((item) => {
      if (seenLabels.has(item.display_name)) return;
      seenLabels.add(item.display_name);
      suggestions.push({
        label: item.display_name,
        latitude: parseFloat(item.lat),
        longitude: parseFloat(item.lon),
      });
    });
    return suggestions;
  } catch (error) {
    console.warn("No se pudieron obtener sugerencias de dirección:", error);
    return [];
  }
};

export const formatDate = (date) => {
  if (date instanceof Date) {
    return date.toLocaleDateString();
  } else if (date && date.toDate instanceof Function) {
    return date.toDate().toLocaleDateString();
  }
  return "";
};


export const handleAttend = async (eventId) => {
  try {
    const user = auth.currentUser;
    if (user) {
      const eventDocRef = doc(db, "events", eventId);
      const eventDocSnap = await getDoc(eventDocRef);

      if (eventDocSnap.exists()) {
        const eventData = eventDocSnap.data();
        const attendees = eventData.attendees || [];

        const updatedAttendees = attendees.includes(user.uid)
          ? attendees.filter((attendeeId) => attendeeId !== user.uid)
          : [...attendees, user.uid];

        await updateDoc(eventDocRef, { attendees: updatedAttendees });
      }
    }
  } catch (error) {
    console.error("Error al manejar la asistencia al evento:", error);
  }
};

// Transacción atómica en vez de getDoc + updateDoc sueltos, mismo motivo que
// handleMeSirve en homeUtils.js: evita que dos clicks casi simultáneos
// dupliquen el increment() del contador.
export const handleMeInteresaEvent = async (
  eventId,
  userAuthenticated,
  userId,
  setUserLikes
) => {
  if (!userAuthenticated) return;

  const eventRef = doc(db, "events", eventId);
  const userLikesRef = doc(db, "userEventLikes", userId);

  try {
    let nowLiked = false;

    await runTransaction(db, async (transaction) => {
      const eventDoc = await transaction.get(eventRef);
      const currentLikes = eventDoc.data()?.likes || [];
      nowLiked = !currentLikes.includes(userId);

      if (nowLiked) {
        transaction.update(eventRef, {
          likes: arrayUnion(userId),
          helpedCount: increment(1),
        });
        transaction.set(userLikesRef, { [eventId]: true }, { merge: true });
      } else {
        transaction.update(eventRef, {
          likes: arrayRemove(userId),
          helpedCount: increment(-1),
        });
        transaction.set(userLikesRef, { [eventId]: deleteField() }, { merge: true });
      }
    });

    setUserLikes((prevLikes) => ({
      ...prevLikes,
      [eventId]: nowLiked,
    }));
  } catch (error) {
    console.error("Error al actualizar 'Me interesa':", error);
  }
};

export const fetchUserEventLikes = async (userAuthenticated, setUserLikes) => {
  if (userAuthenticated && auth.currentUser) {
    const userLikesRef = doc(db, "userEventLikes", auth.currentUser.uid);
    const userLikesDoc = await getDoc(userLikesRef);
    if (userLikesDoc.exists()) {
      setUserLikes(userLikesDoc.data());
    }
  }
};

export const fetchAttendees = async (events, setAttendees, setAttendeeProfiles) => {
  const attendeesData = {};
  const profilesData = {};

  // Cada lectura falla por separado: un evento que ya se borró (las reglas
  // niegan leer un documento inexistente) o un perfil que no se puede leer
  // no deben dejar sin asistentes al resto. Los perfiles (/users) solo se
  // pueden leer con sesión iniciada.
  const fetchPromises = events.map(async (event) => {
    let eventDocSnap;
    try {
      eventDocSnap = await getDoc(doc(db, "events", event.id));
    } catch (error) {
      attendeesData[event.id] = event.attendees || [];
      return;
    }

    if (eventDocSnap.exists()) {
      const eventData = eventDocSnap.data();
      attendeesData[event.id] = eventData.attendees || [];
      if (!auth.currentUser) return;

      const attendeePromises = attendeesData[event.id].map(async (attendeeId) => {
        if (!profilesData[attendeeId]) {
          try {
            const userDocSnap = await getDoc(doc(db, "users", attendeeId));
            if (userDocSnap.exists()) {
              const userData = userDocSnap.data();
              profilesData[attendeeId] = {
                profileImageUrl: userData.profileImageUrl || "",
                name: userData.name || userData.displayName || "Usuario",
              };
            }
          } catch (error) {
            // Perfil no disponible: el asistente se muestra sin foto ni nombre.
          }
        }
      });

      await Promise.all(attendeePromises);
    }
  });

  await Promise.all(fetchPromises);

  setAttendees(attendeesData);
  setAttendeeProfiles(profilesData);
};

export const handleFollow = async (companyId, following, setFollowing, setLoading) => {
  try {
    const { uid } = auth.currentUser || {};
    if (uid) {
      setLoading(true);
      const userFollowingRef = doc(db, "userFollowing", uid);
      const companyFollowersRef = doc(db, "companyFollowers", companyId);

      const isFollowing = following.includes(companyId);

      if (isFollowing) {
        await updateDoc(userFollowingRef, {
          [companyId]: deleteField(),
        });
        await updateDoc(companyFollowersRef, {
          [uid]: deleteField(),
        });
        setFollowing(following.filter((id) => id !== companyId));
      } else {
        await setDoc(userFollowingRef, { [companyId]: true }, { merge: true });
        await setDoc(companyFollowersRef, { [uid]: true }, { merge: true });
        setFollowing([...following, companyId]);
      }
      setLoading(false);
    }
  } catch (error) {
    console.error("Error al manejar el seguimiento de la empresa:", error);
    setLoading(false);
  }
};

export const fetchFollowing = async (setFollowing, setLoading) => {
  try {
    const { uid } = auth.currentUser || {};
    if (uid) {
      const userFollowingRef = doc(db, "userFollowing", uid);
      const userFollowingDoc = await getDoc(userFollowingRef);
      
      if (userFollowingDoc.exists()) {
        const followingData = userFollowingDoc.data();
        const followingIds = Object.keys(followingData);
        setFollowing(followingIds);
      } else {
        setFollowing([]);
      }
    }
    setLoading(false);
  } catch (error) {
    console.error("Error al obtener las empresas seguidas:", error);
    setLoading(false);
  }
};

export const fetchEvents = async (setLoading, setEvents) => {
  try {
    setLoading(true);
    const eventsCollectionRef = collection(db, "events");
    const unsubscribe = onSnapshot(
      query(eventsCollectionRef, where("isPrivate", "==", false), orderBy("createdAt", "desc")),
      (snapshot) => {
        const eventsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLoading(false);
        setEvents(eventsData);
      }
    );
    return unsubscribe;
  } catch (error) {
    console.error("Error al obtener eventos:", error);
    setLoading(false);
    setEvents([]); // Asegurarse de establecer un array vacío en caso de error
  }
};

// Q&A de eventos: mismo esquema que el de ofertas (questions/answers en
// homeUtils.js) pero en colecciones separadas (eventQuestions/eventAnswers)
// filtradas por eventId, en vez de reusar las de ofertas por offerId.
export const postEventQuestion = async (eventId, userId, questionText) => {
  try {
    await addDoc(collection(db, "eventQuestions"), {
      eventId,
      userId,
      question: questionText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la pregunta: ", error);
  }
};

export const postEventAnswer = async (questionId, userId, answerText, eventId) => {
  try {
    await addDoc(collection(db, "eventAnswers"), {
      questionId,
      userId,
      eventId,
      answer: answerText,
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error al publicar la respuesta: ", error);
  }
};

export const fetchEventQuestionsAndAnswersWithUserData = (
  eventId,
  setQuestionsAndAnswers
) => {
  const q = query(
    collection(db, "eventQuestions"),
    where("eventId", "==", eventId),
    orderBy("createdAt", "asc")
  );

  const unsubscribe = onSnapshot(q, async (snapshot) => {
    const questionsAndAnswers = [];
    for (const questionDoc of snapshot.docs) {
      const questionData = questionDoc.data();

      const userDocRef = doc(db, "users", questionData.userId);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();

      const answersQuery = query(
        collection(db, "eventAnswers"),
        where("questionId", "==", questionDoc.id)
      );
      const answersSnapshot = await getDocs(answersQuery);
      const answers = await Promise.all(
        answersSnapshot.docs.map(async (answerDoc) => {
          const answerData = answerDoc.data();

          const answerUserDocRef = doc(db, "users", answerData.userId);
          const answerUserDocSnap = await getDoc(answerUserDocRef);
          const answerUserData = answerUserDocSnap.data();

          return {
            id: answerDoc.id,
            ...answerData,
            displayName: answerUserData.displayName,
            profileImageUrl: answerUserData.profileImageUrl,
          };
        })
      );

      questionsAndAnswers.push({
        id: questionDoc.id,
        ...questionData,
        displayName: userData.displayName,
        profileImageUrl: userData.profileImageUrl,
        answers: answers,
      });
    }

    setQuestionsAndAnswers(questionsAndAnswers);
  });

  return unsubscribe;
};

// "Guardados": lista privada de eventos para revisar después, mismo patrón
// que toggleSaveOffer/fetchSavedOfferIds en homeUtils.js.
export const toggleSaveEvent = async (userId, eventId, isSaved) => {
  if (!userId || !eventId) return false;
  try {
    const savedRef = doc(db, "savedEvents", userId);
    if (isSaved) {
      await updateDoc(savedRef, { [eventId]: deleteField() });
    } else {
      await setDoc(savedRef, { [eventId]: true }, { merge: true });
    }
    return true;
  } catch (error) {
    console.error("Error al guardar/quitar el evento:", error);
    return false;
  }
};

// Trae los eventos completos por ID (a diferencia de fetchSavedEventIds, que
// solo da los IDs) — mismo patrón que fetchOffersByIds en homeUtils.js.
// Va de a un getDoc por evento: una consulta where(documentId(), "in", ...)
// no la pueden validar las reglas (podría incluir un evento privado ajeno) y
// fallaría entera; así solo se pierde el evento que no se puede leer.
export const fetchEventsByIds = async (eventIds) => {
  const uniqueIds = [...new Set(eventIds)].filter(Boolean);
  const snaps = await Promise.allSettled(uniqueIds.map((id) => getDoc(doc(db, "events", id))));
  return snaps
    .filter((r) => r.status === "fulfilled" && r.value.exists())
    .map((r) => ({ id: r.value.id, ...r.value.data() }));
};

export const fetchSavedEventIds = async (userId) => {
  if (!userId) return [];
  try {
    const savedRef = doc(db, "savedEvents", userId);
    const savedDoc = await getDoc(savedRef);
    return savedDoc.exists() ? Object.keys(savedDoc.data()) : [];
  } catch (error) {
    console.error("Error al obtener los eventos guardados:", error);
    return [];
  }
};

// "No me interesa": oculta un evento puntual del feed de quien lo oculta,
// mismo patrón doc-por-usuario que hideOffer en homeUtils.js.
export const hideEvent = async (userId, eventId) => {
  if (!userId || !eventId) return false;
  try {
    await setDoc(doc(db, "hiddenEvents", userId), { [eventId]: true }, { merge: true });
    return true;
  } catch (error) {
    console.error("Error al ocultar el evento:", error);
    return false;
  }
};

export const fetchHiddenEventIds = async (userId) => {
  if (!userId) return [];
  try {
    const snap = await getDoc(doc(db, "hiddenEvents", userId));
    return snap.exists() ? Object.keys(snap.data()) : [];
  } catch (error) {
    console.error("Error al obtener eventos ocultos:", error);
    return [];
  }
};

// Selector de participantes para eventos privados (publishEvents.js): busca
// por prefijo de displayName, igual que se hace con /users en otras partes
// de la app (todo usuario logueado puede leer /users, ver firestore.rules).
export const searchUsersByName = async (term, excludeUid) => {
  const cleanTerm = term.trim();
  if (cleanTerm.length < 2) return [];
  try {
    const usersQuery = query(
      collection(db, "users"),
      orderBy("displayName"),
      startAt(cleanTerm),
      endAt(cleanTerm + ""),
      limit(10)
    );
    const snapshot = await getDocs(usersQuery);
    return snapshot.docs
      .map((docSnap) => ({ uid: docSnap.id, ...docSnap.data() }))
      .filter((user) => user.uid !== excludeUid)
      .map((user) => ({
        uid: user.uid,
        displayName: user.displayName || user.name || "Usuario",
        profileImageUrl: user.profileImageUrl || "",
      }));
  } catch (error) {
    console.error("Error al buscar usuarios:", error);
    return [];
  }
};

// Registra la compra de una entrada paga (ver publishEvents.js / events.js):
// el pago ya fue capturado 100% al PayPal del organizador (payeeEmail). La
// Cloud Function recordPayPalPayment verifica la orden con PayPal, guarda la
// venta en ticketSales (para la comisión) y marca al comprador como
// asistente/ticketBuyer del evento; el cliente ya no puede escribir eso.
export const recordTicketSale = async ({ eventId, paypalTransactionId }) => {
  await functions.httpsCallable("recordPayPalPayment")({
    kind: "ticket",
    targetId: eventId,
    orderId: paypalTransactionId,
  });
};


// Comisión (5% de referencia) todavía no cobrada de este evento: mismo
// criterio que getPendingCommission (storesUtils.js) pero sobre ticketSales.
export const getPendingEventCommission = async (eventId, sellerId) => {
  if (!eventId || !sellerId) return { amount: 0, salesCount: 0 };
  try {
    const paymentsSnap = await getDocs(
      query(
        collection(db, "eventCommissionPayments"),
        where("eventId", "==", eventId),
        where("sellerId", "==", sellerId)
      )
    );

    let lastCoveredUpTo = null;
    paymentsSnap.forEach((docSnap) => {
      const coveredUpTo = toJsDate(docSnap.data().coveredUpTo);
      if (coveredUpTo && (!lastCoveredUpTo || coveredUpTo > lastCoveredUpTo)) {
        lastCoveredUpTo = coveredUpTo;
      }
    });

    const salesSnap = await getDocs(
      query(
        collection(db, "ticketSales"),
        where("eventId", "==", eventId),
        where("sellerId", "==", sellerId)
      )
    );

    let amount = 0;
    let salesCount = 0;
    salesSnap.forEach((docSnap) => {
      const sale = docSnap.data();
      const purchaseDate = toJsDate(sale.purchaseDate);
      if (lastCoveredUpTo && purchaseDate && purchaseDate <= lastCoveredUpTo) return;
      amount += Number(sale.commissionAmount) || 0;
      salesCount += 1;
    });

    return { amount, salesCount };
  } catch (error) {
    console.error("Error al calcular la comisión pendiente del evento:", error);
    return { amount: 0, salesCount: 0 };
  }
};

// Lo registra recordPayPalPayment después de verificar el pago con PayPal.
export const markEventCommissionPaid = async ({ eventId, paypalTransactionId }) => {
  await functions.httpsCallable("recordPayPalPayment")({
    kind: "eventCommission",
    targetId: eventId,
    orderId: paypalTransactionId,
  });
};